import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../common/VFX-UI";

//App
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../utility/constants/constants";
import { SearchValue } from "../../store/types";
import {
  OrganizationContextData,
  OrganizationUseState,
  SortBy,
} from "../../types";
import organizationApi from "../../app/model/api/organizationApi";
import { queryKeys } from "./constants";
import Organizations from "./Organizations";
import {
  selectIsActive,
  selectSearchValue,
  setIsActive,
  setSearchValue,
} from "../../store/organization";

export const OrganizationContext =
  React.createContext<OrganizationContextData | null>(null);

const OrganizationsContainer = () => {
  const dispatch = useDispatch();

  const [state, setState] = useState<OrganizationUseState>({
    stateId: null,
    organizationId: null,
    pageSize: GRID_PAGE_SIZE,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    currentPage: 1,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const organizationGridQuery = useQuery({
    queryKey: [queryKeys.getOrganizations],
    queryFn: () =>
      organizationApi.searchOrganizations({
        ...state.searchPayload,
        isActive: state.activeToggle,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
        sortBy: state.sortBy,
      }),
  });

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await organizationApi.deactivate(value);
    },
    onError: () => console.log("error"),
    onSuccess: () => console.log("success"),
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await organizationApi.activate(value);
    },
    onError: () => console.log("error"),
    onSuccess: () => console.log("success"),
  });

  const updateOrganizationId = async (organizationId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      organizationId: organizationId,
    }));
  };

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const updateStateId = async (stateId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      stateId: stateId,
    }));
  };

  const getOrganizationQuery = useQuery({
    queryKey: [queryKeys.getOrganization, state.organizationId],
    queryFn: () => organizationApi.get(state.organizationId ?? EMPTY_GUID),
    enabled: state.organizationId !== null,
  });

  const contextValue: OrganizationContextData = {
    data: getOrganizationQuery?.data?.result,
    id: state.organizationId,
    stateId: state.stateId,
    setStateId: updateStateId,
    activeToggle: state.activeToggle,
    setId: updateOrganizationId,
    refetchGrid: () => organizationGridQuery.refetch(),
    setActiveToggleValue: updateActiveToggle,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    setSortBy: updateSortBy,
    paging: {
      currentPage: state.currentPage,
      totalSize: organizationGridQuery.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    refetchOrganization: () => getOrganizationQuery.refetch(),
  };

  return organizationGridQuery.isLoading ? (
    <LoadingIndicator />
  ) : (
    <OrganizationContext.Provider value={contextValue}>
      <Organizations
        data={organizationGridQuery?.data?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </OrganizationContext.Provider>
  );
};

export default OrganizationsContainer;
